import type { SignedManifestEnvelope } from "../images/manifest";
import type { SupportedModel } from "../models/supported";
import type { AuthProvider } from "./auth";

/**
 * The ONLY place the installer's REST shapes live. Installer-specific routes follow
 * installer/docs/cloud-api-contract.md; line-group routes are coded verbatim against
 * ../../docs/group-api-contract.md (the cloud implements both). The CLI mirrors this file in
 * qh_install/api.py.
 */

export interface Me {
  business_id: string;
  business_name: string;
  user_email: string;
}
export interface ModelsResponse {
  models: SupportedModel[];
  purchase_spec: { en: string; ar: string };
}

// ---- line groups (docs/group-api-contract.md "Model") ----------------------------------

export type FwdReason = "busy" | "no_reply" | "not_reachable" | "unconditional";
export interface FwdRule {
  reason: FwdReason;
  enabled: boolean;
  target?: string;
  no_reply_timer_s?: number;
}
/** Mirrors protocol 1.2 `line.forwarding.state` (`source`, `rules`, `call_waiting`). */
export interface FwdState {
  call_waiting: boolean;
  rules: FwdRule[];
  source?: "network_query" | "unverified";
}
export type MemberSync = "in_sync" | "out_of_sync" | "unverified" | "repairing";
export interface MemberForwarding {
  desired: FwdState;
  actual: FwdState | null;
  sync: MemberSync;
  last_readback_at?: string | null;
  last_error?: string | null;
}
export type MemberRole = "main" | "middle" | "last";
export type LineState = "idle" | "ringing" | "dialing" | "in_call" | "in_call_waiting" | "on_hold" | "offline";
export interface Member {
  position: number;
  role: MemberRole;
  device_id: string | null;
  sim_slot?: number;
  number: string;
  licence_attested: boolean;
  attested_by?: string | null;
  attested_at?: string | null;
  forwarding: MemberForwarding;
  line_state?: LineState;
  repair?: { active: boolean; reason?: string | null; since?: string | null };
}
export type OverflowPolicy = "announce_and_callback" | "reject_and_callback" | "human";
export interface Group {
  id: string;
  business_id: string;
  name: string;
  main_number: string;
  members: Member[];
  overflow_policy?: OverflowPolicy;
  overflow_ring_s?: number;
  overflow_after_s?: number;
  max_hold_s?: number;
  no_reply_timer_s?: number;
  prefer_main_wait_s?: number;
  reserve_inbound_members?: number[];
  status: "ok" | "degraded" | "unverified";
  created_at?: string;
  updated_at?: string;
}
/** `GET /installer/groups`: the business's groups with member counts + SIM requirements text. */
export interface InstallerGroupsResponse {
  groups: { id: string; name: string; main_number: string; member_count: number; status: Group["status"] }[];
  /** Per-member SIM requirements (Part A2b), shown next to the group picker. */
  sim_requirements: { en: string; ar: string };
}
export type JobState = "pending" | "reprogramming" | "awaiting_readback" | "done" | "failed";
export interface JobStep {
  member_position: number;
  action: string;
  state: string;
  detail?: string | null;
}
export interface GroupJob {
  job_id?: string;
  state: JobState;
  steps: JobStep[];
  error?: string | null;
}
export interface AddMemberInput {
  number: string;
  sim_slot?: number;
  licence_attested: true;
  attested_by: string;
  device_id?: string;
  enrollment_id?: string;
}
/**
 * `POST /groups/{id}/members` and `…/replace` return a `Member` per the contract. The contract
 * does not say how the installer learns the job id of the reprogramming it "kicks off"; we
 * read an optional `job_id` and otherwise fall back to `POST /groups/{id}/reconcile`
 * (see PROTOCOL_GAPS.md "group contract asks").
 */
export type MemberWithJob = Member & { job_id?: string };
export interface RemoveResponse {
  job_id: string;
  /** Present with `?force=true`: the operator code the customer dials from any phone. */
  clear_instruction?: string;
  requires_confirm_cleared?: boolean;
}
export interface HopTestInput {
  caller_number: string;
  hops?: number[];
  test_no_reply?: boolean;
}
export interface HopStep {
  hop: number;
  expected_member: number;
  rang_member: number | null;
  time_to_ring_ms: number | null;
  forwarded_from: number | null;
  ok: boolean;
}
export interface HopTestResult {
  job_id?: string;
  /** Terminal states are `done` and `failed`; anything else is still running. */
  state: string;
  steps: HopStep[];
  no_reply?: { expected_member?: number; rang_member?: number | null; timer_s?: number; time_to_ring_ms?: number | null; ok: boolean } | null;
  error?: string | null;
}
export interface AuditEntry {
  ts: string;
  actor: string;
  action: string;
  detail: unknown;
}

// ---- installer routes (installer/docs/cloud-api-contract.md) --------------------------

export interface EnrollmentToken {
  enrollment_id: string;
  /** Human-typable one-time token, e.g. QH-7K3M-P9X2-4RTD. */
  token: string;
  /** Exactly what the phone's Setup screen scans; also rendered as a QR by the page. */
  qr_payload: string;
  expires_at: string;
}
export interface DeviceCapabilities {
  capture_downlink: boolean;
  capture_uplink: boolean;
  capture_mixed: boolean;
  uplink_injection: boolean;
  call_screening_mode: boolean;
  hold_supported?: boolean;
  call_waiting_delivered?: boolean;
  forwarding_api?: "system_api" | "mmi_only" | "none";
  audio_follows_active_call?: boolean;
  model: string;
  soc: string;
  ram_gb?: number;
}
export type EnrollmentState = "pending" | "token_consumed" | "enrolled" | "probed" | "live" | "expired" | "failed";
export interface EnrollmentStatus {
  enrollment_id: string;
  state: EnrollmentState;
  device_id: string | null;
  serialno: string | null;
  capabilities: DeviceCapabilities | null;
  group_id: string;
  position: number | null;
  forwarding_sync: MemberSync | null;
  /** SIM number as read by the phone at enrollment, when the cloud has it (used as a prefill). */
  sim_number?: string | null;
  error: string | null;
}
export interface SupportLogUpload {
  session_id: string;
  model: string | null;
  serialno: string | null;
  user_agent: string;
  entries: unknown[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiOptions {
  baseUrl: string;
  auth: AuthProvider;
  fetchFn?: typeof fetch;
}

export class InstallerApi {
  private readonly fetchFn: typeof fetch;
  readonly baseUrl: string;

  constructor(private readonly o: ApiOptions) {
    this.fetchFn = o.fetchFn ?? fetch;
    this.baseUrl = o.baseUrl.replace(/\/+$/, "");
  }

  get auth(): AuthProvider {
    return this.o.auth;
  }

  private async call<T>(method: string, path: string, body?: unknown, opts: { anon?: boolean } = {}): Promise<T> {
    const headers: Record<string, string> = { accept: "application/json" };
    if (!opts.anon) {
      const token = await this.o.auth.getToken();
      if (!token) throw new ApiError(401, "unauthenticated", "not signed in");
      headers["authorization"] = `Bearer ${token}`;
    }
    if (body !== undefined) headers["content-type"] = "application/json";
    const res = await this.fetchFn(`${this.baseUrl}${path}`, { method, headers, body: body === undefined ? null : JSON.stringify(body) });
    if (!res.ok) {
      let code = "http_error";
      let msg = `HTTP ${res.status}`;
      try {
        const j = (await res.json()) as { error?: { code?: string; message?: string } };
        if (j.error?.code) code = j.error.code;
        if (j.error?.message) msg = j.error.message;
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(res.status, code, msg);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  me(): Promise<Me> {
    return this.call("GET", "/me");
  }
  /** Public: the purchase spec is shown before sign-in. */
  models(): Promise<ModelsResponse> {
    return this.call("GET", "/installer/models", undefined, { anon: true });
  }
  manifest(model: string, channel: "quickhub" | "stock" = "quickhub"): Promise<SignedManifestEnvelope> {
    return this.call("GET", `/installer/images/${encodeURIComponent(model)}/manifest?channel=${channel}`);
  }
  createEnrollmentToken(input: { group_id: string; position?: number; model: string; serialno: string }): Promise<EnrollmentToken> {
    return this.call("POST", "/installer/enrollment-tokens", input);
  }
  enrollmentStatus(enrollmentId: string): Promise<EnrollmentStatus> {
    return this.call("GET", `/installer/enrollments/${encodeURIComponent(enrollmentId)}`);
  }
  uploadSupportLogs(upload: SupportLogUpload): Promise<{ ticket_id: string }> {
    return this.call("POST", "/installer/support-logs", upload);
  }

  // ---- line groups ------------------------------------------------------------------

  installerGroups(): Promise<InstallerGroupsResponse> {
    return this.call("GET", "/installer/groups");
  }
  createGroup(input: { name: string; main_number: string; overflow_policy?: OverflowPolicy }): Promise<Group> {
    return this.call("POST", "/groups", input);
  }
  getGroup(id: string): Promise<Group> {
    return this.call("GET", `/groups/${encodeURIComponent(id)}`);
  }
  addMember(groupId: string, input: AddMemberInput): Promise<MemberWithJob> {
    return this.call("POST", `/groups/${encodeURIComponent(groupId)}/members`, input);
  }
  replaceMember(groupId: string, position: number, input: { device_id: string; number?: string }): Promise<MemberWithJob> {
    return this.call("POST", `/groups/${encodeURIComponent(groupId)}/members/${position}/replace`, input);
  }
  removeMember(groupId: string, position: number, force = false): Promise<RemoveResponse> {
    return this.call("DELETE", `/groups/${encodeURIComponent(groupId)}/members/${position}${force ? "?force=true" : ""}`);
  }
  confirmCleared(groupId: string, position: number): Promise<unknown> {
    return this.call("POST", `/groups/${encodeURIComponent(groupId)}/members/${position}/confirm-cleared`, { confirm_cleared: true });
  }
  groupJob(groupId: string, jobId: string): Promise<GroupJob> {
    return this.call("GET", `/groups/${encodeURIComponent(groupId)}/jobs/${encodeURIComponent(jobId)}`);
  }
  reconcile(groupId: string): Promise<{ job_id: string }> {
    return this.call("POST", `/groups/${encodeURIComponent(groupId)}/reconcile`);
  }
  startHopTest(groupId: string, input: HopTestInput): Promise<{ job_id: string }> {
    return this.call("POST", `/groups/${encodeURIComponent(groupId)}/hop_test`, input);
  }
  hopTest(groupId: string, jobId: string): Promise<HopTestResult> {
    return this.call("GET", `/groups/${encodeURIComponent(groupId)}/hop_test/${encodeURIComponent(jobId)}`);
  }
  groupAudit(groupId: string): Promise<AuditEntry[]> {
    return this.call("GET", `/groups/${encodeURIComponent(groupId)}/audit`);
  }
  memberForwarding(groupId: string, position: number): Promise<MemberForwarding> {
    return this.call("GET", `/groups/${encodeURIComponent(groupId)}/members/${position}/forwarding`);
  }
}
